import React, { useState, useEffect } from 'react';
import { 
  Box, Typography, Paper, Grid, Tabs, Tab, Badge, 
  List, ListItem, ListItemText, ListItemAvatar, Avatar,
  Chip, Divider, TextField, Button, IconButton,
  Dialog, DialogTitle, DialogContent, DialogActions,
  FormControl, InputLabel, Select, MenuItem
} from '@mui/material';
import { 
  FilterList, Refresh, Search, Assignment, 
  Message, AttachFile, Close,
  ErrorOutline, Alarm, Info
} from '@mui/icons-material';

// Mock data for tickets - in production this would come from API
const MOCK_TICKETS = [
  { 
    id: 1, 
    user: { id: 101, name: 'John Doe', avatar: '' },
    subject: 'API connection issues',
    description: 'I cannot connect my trading account to the platform.',
    status: 'open',
    priority: 'high',
    category: 'technical',
    created: '2025-05-15T10:30:00',
    updated: '2025-05-16T09:15:00',
    assignee: 'Admin',
    messages: [
      { 
        id: 1, 
        sender: { id: 101, name: 'John Doe', avatar: '', type: 'user' },
        content: 'I\'ve been trying to connect my trading account but keep getting error code 403.',
        timestamp: '2025-05-15T10:30:00',
        attachments: []
      },
      {
        id: 2,
        sender: { id: 1, name: 'Admin', avatar: '', type: 'admin' },
        content: 'Could you please provide your account ID and the exact error message you\'re seeing?',
        timestamp: '2025-05-15T11:45:00',
        attachments: []
      },
      {
        id: 3,
        sender: { id: 101, name: 'John Doe', avatar: '', type: 'user' },
        content: 'My account ID is TD-58493. The error says "API key validation failed".',
        timestamp: '2025-05-16T09:15:00',
        attachments: []
      }
    ]
  },
  { 
    id: 2, 
    user: { id: 102, name: 'Jane Smith', avatar: '' },
    subject: 'Billing question',
    description: 'I was charged twice for my subscription.',
    status: 'in-progress',
    priority: 'medium',
    category: 'billing',
    created: '2025-05-14T15:20:00',
    updated: '2025-05-15T12:30:00',
    assignee: 'Admin',
    messages: [
      { 
        id: 1, 
        sender: { id: 102, name: 'Jane Smith', avatar: '', type: 'user' },
        content: 'I noticed two charges on my credit card for the monthly subscription.',
        timestamp: '2025-05-14T15:20:00',
        attachments: []
      }
    ]
  },
  { 
    id: 3, 
    user: { id: 103, name: 'Robert Johnson', avatar: '' },
    subject: 'Strategy not working',
    description: 'My custom strategy is not executing trades.',
    status: 'closed',
    priority: 'low',
    category: 'trading',
    created: '2025-05-10T09:00:00',
    updated: '2025-05-12T14:45:00',
    assignee: 'Admin',
    messages: [
      { 
        id: 1, 
        sender: { id: 103, name: 'Robert Johnson', avatar: '', type: 'user' },
        content: 'My strategy called "MACD Crossover" is not executing any trades.',
        timestamp: '2025-05-10T09:00:00',
        attachments: []
      }
    ]
  },
];

// Status colors for visual indicators
const STATUS_COLORS = {
  'open': '#f44336', // Red
  'in-progress': '#ff9800', // Orange
  'pending': '#2196f3', // Blue
  'resolved': '#4caf50', // Green
  'closed': '#9e9e9e' // Grey
};

// Priority icons for visual indicators
const PRIORITY_ICONS = {
  'high': <ErrorOutline style={{ color: '#f44336' }} />,
  'medium': <Alarm style={{ color: '#ff9800' }} />,
  'low': <Info style={{ color: '#2196f3' }} />
};

const TicketManagement = () => {
  const [tickets, setTickets] = useState(MOCK_TICKETS);
  const [filteredTickets, setFilteredTickets] = useState(MOCK_TICKETS);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');
  const [replyText, setReplyText] = useState('');
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    priority: '',
    category: ''
  });

  // Effect to filter tickets based on tab, search query, and filters
  useEffect(() => {
    let filtered = [...tickets];
    
    // Apply tab filters
    if (tabValue === 1) filtered = filtered.filter(ticket => ticket.status === 'open');
    if (tabValue === 2) filtered = filtered.filter(ticket => ticket.status === 'in-progress');
    if (tabValue === 3) filtered = filtered.filter(ticket => ticket.status === 'closed' || ticket.status === 'resolved');
    
    // Apply search query
    if (searchQuery) {
      filtered = filtered.filter(ticket => 
        ticket.subject.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ticket.user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        ticket.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    // Apply custom filters
    if (filters.status) filtered = filtered.filter(ticket => ticket.status === filters.status);
    if (filters.priority) filtered = filtered.filter(ticket => ticket.priority === filters.priority);
    if (filters.category) filtered = filtered.filter(ticket => ticket.category === filters.category);
    
    setFilteredTickets(filtered);
  }, [tickets, tabValue, searchQuery, filters]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleTicketSelect = (ticket) => {
    setSelectedTicket(ticket);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleReplyChange = (e) => {
    setReplyText(e.target.value);
  };

  const handleSendReply = () => {
    if (!replyText.trim()) return;
    
    const newMessage = {
      id: selectedTicket.messages.length + 1,
      sender: { id: 1, name: 'Admin', avatar: '', type: 'admin' },
      content: replyText,
      timestamp: new Date().toISOString(),
      attachments: []
    };
    
    const updatedTickets = tickets.map(ticket => {
      if (ticket.id === selectedTicket.id) {
        return {
          ...ticket,
          messages: [...ticket.messages, newMessage],
          updated: new Date().toISOString()
        };
      }
      return ticket;
    });
    
    setTickets(updatedTickets);
    setSelectedTicket({
      ...selectedTicket,
      messages: [...selectedTicket.messages, newMessage],
      updated: new Date().toISOString()
    });
    setReplyText('');
  };

  const handleStatusChange = (newStatus) => {
    const updatedTickets = tickets.map(ticket => {
      if (ticket.id === selectedTicket.id) {
        return {
          ...ticket,
          status: newStatus,
          updated: new Date().toISOString()
        };
      }
      return ticket;
    });
    
    setTickets(updatedTickets);
    setSelectedTicket({
      ...selectedTicket,
      status: newStatus,
      updated: new Date().toISOString()
    });
  };

  const handlePriorityChange = (newPriority) => {
    const updatedTickets = tickets.map(ticket => {
      if (ticket.id === selectedTicket.id) {
        return {
          ...ticket,
          priority: newPriority,
          updated: new Date().toISOString()
        };
      }
      return ticket;
    });
    
    setTickets(updatedTickets);
    setSelectedTicket({
      ...selectedTicket,
      priority: newPriority,
      updated: new Date().toISOString()
    });
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  const clearFilters = () => {
    setFilters({
      status: '',
      priority: '',
      category: ''
    });
  };

  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Support Ticket Management
      </Typography>
      
      <Grid container spacing={3}>
        {/* Left side - Ticket List */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, height: '85vh', display: 'flex', flexDirection: 'column' }}>
            {/* Search and Filter */}
            <Box sx={{ mb: 2, display: 'flex', alignItems: 'center' }}>
              <TextField
                size="small"
                placeholder="Search tickets..."
                variant="outlined"
                fullWidth
                value={searchQuery}
                onChange={handleSearchChange}
                InputProps={{
                  startAdornment: <Search color="action" sx={{ mr: 1 }} />
                }}
              />
              <IconButton onClick={() => setFilterDialogOpen(true)} sx={{ ml: 1 }}>
                <FilterList />
              </IconButton>
              <IconButton sx={{ ml: 1 }}>
                <Refresh />
              </IconButton>
            </Box>
            
            {/* Tabs */}
            <Tabs 
              value={tabValue} 
              onChange={handleTabChange}
              variant="fullWidth" 
              sx={{ mb: 2 }}
            >
              <Tab 
                label={
                  <Badge badgeContent={filteredTickets.length} color="primary">
                    All
                  </Badge>
                } 
              />
              <Tab 
                label={
                  <Badge 
                    badgeContent={tickets.filter(t => t.status === 'open').length} 
                    color="error"
                  >
                    Open
                  </Badge>
                } 
              />
              <Tab 
                label={
                  <Badge 
                    badgeContent={tickets.filter(t => t.status === 'in-progress').length} 
                    color="warning"
                  >
                    Active
                  </Badge>
                } 
              />
              <Tab 
                label={
                  <Badge 
                    badgeContent={
                      tickets.filter(t => 
                        t.status === 'closed' || t.status === 'resolved'
                      ).length
                    } 
                    color="success"
                  >
                    Closed
                  </Badge>
                } 
              />
            </Tabs>
            
            {/* Ticket List */}
            <List sx={{ overflow: 'auto', flexGrow: 1 }}>
              {filteredTickets.length > 0 ? (
                filteredTickets.map((ticket) => (
                  <React.Fragment key={ticket.id}>
                    <ListItem 
                      alignItems="flex-start" 
                      button
                      selected={selectedTicket && selectedTicket.id === ticket.id}
                      onClick={() => handleTicketSelect(ticket)}
                      sx={{ 
                        borderLeft: `4px solid ${STATUS_COLORS[ticket.status]}`,
                        '&:hover': { backgroundColor: 'action.hover' },
                        '&.Mui-selected': { backgroundColor: 'action.selected' }
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar>
                          {ticket.user.name.charAt(0)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography component="span" variant="subtitle2" noWrap sx={{ maxWidth: '180px' }}>
                              {ticket.subject}
                            </Typography>
                            {PRIORITY_ICONS[ticket.priority]}
                          </Box>
                        }
                        secondary={
                          <React.Fragment>
                            <Typography component="span" variant="body2" color="text.primary" noWrap display="block">
                              {ticket.user.name}
                            </Typography>
                            <Typography component="span" variant="caption" color="text.secondary" noWrap display="block">
                              {formatDate(ticket.updated)}
                            </Typography>
                          </React.Fragment>
                        }
                      />
                      <Box sx={{ mt: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5, ml: 56 }}>
                        <Chip 
                          size="small" 
                          label={ticket.status.replace('-', ' ')} 
                          sx={{ 
                            backgroundColor: `${STATUS_COLORS[ticket.status]}20`,
                            color: STATUS_COLORS[ticket.status],
                            textTransform: 'capitalize'
                          }}
                        />
                        <Chip 
                          size="small" 
                          label={ticket.category} 
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </Box>
                    </ListItem>
                    <Divider component="li" />
                  </React.Fragment>
                ))
              ) : (
                <Box sx={{ p: 2, textAlign: 'center' }}>
                  <Assignment sx={{ fontSize: 40, color: 'text.secondary', mb: 1 }} />
                  <Typography variant="body1" color="text.secondary">
                    No tickets found
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Try adjusting your filters
                  </Typography>
                </Box>
              )}
            </List>
          </Paper>
        </Grid>
        
        {/* Right side - Ticket Details */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2, height: '85vh', display: 'flex', flexDirection: 'column' }}>
            {selectedTicket ? (
              <React.Fragment>
                {/* Ticket Header */}
                <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="h6">{selectedTicket.subject}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Ticket #{selectedTicket.id} • Opened by {selectedTicket.user.name} on {formatDate(selectedTicket.created)}
                    </Typography>
                  </Box>
                  <Box>
                    <FormControl size="small" sx={{ minWidth: 120, mr: 1 }}>
                      <InputLabel>Status</InputLabel>
                      <Select
                        value={selectedTicket.status}
                        label="Status"
                        onChange={(e) => handleStatusChange(e.target.value)}
                      >
                        <MenuItem value="open">Open</MenuItem>
                        <MenuItem value="in-progress">In Progress</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="resolved">Resolved</MenuItem>
                        <MenuItem value="closed">Closed</MenuItem>
                      </Select>
                    </FormControl>
                    <FormControl size="small" sx={{ minWidth: 120 }}>
                      <InputLabel>Priority</InputLabel>
                      <Select
                        value={selectedTicket.priority}
                        label="Priority"
                        onChange={(e) => handlePriorityChange(e.target.value)}
                      >
                        <MenuItem value="high">High</MenuItem>
                        <MenuItem value="medium">Medium</MenuItem>
                        <MenuItem value="low">Low</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                </Box>
                <Divider sx={{ mb: 2 }} />
                
                {/* Message Thread */}
                <Box sx={{ flexGrow: 1, overflow: 'auto', mb: 2 }}>
                  {selectedTicket.messages.map((message) => (
                    <Box 
                      key={message.id}
                      sx={{ 
                        mb: 2, 
                        p: 2, 
                        backgroundColor: (theme) => (
                          message.sender.type === 'admin'
                            ? (theme.palette.mode === 'dark' ? theme.palette.primary.dark : theme.palette.primary.light)
                            : (theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100])
                        ),
                        color: (theme) => (
                          message.sender.type === 'admin'
                            ? (theme.palette.mode === 'dark' 
                                ? theme.palette.primary.contrastText 
                                : theme.palette.getContrastText(theme.palette.primary.light))
                            : theme.palette.getContrastText(
                                theme.palette.mode === 'dark' ? theme.palette.grey[800] : theme.palette.grey[100]
                              )
                        ),
                        borderRadius: 2
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Avatar sx={{ width: 32, height: 32, mr: 1 }}>
                          {message.sender.name.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle2">
                            {message.sender.name}
                            {message.sender.type === 'admin' && (
                              <Chip 
                                size="small" 
                                label="Staff" 
                                sx={{ ml: 1, backgroundColor: '#4caf50', color: 'white' }}
                              />
                            )}
                          </Typography>
                          <Typography variant="caption" sx={{ color: 'inherit', opacity: 0.8 }}>
                            {formatDate(message.timestamp)}
                          </Typography>
                        </Box>
                      </Box>
                      <Typography variant="body2">{message.content}</Typography>
                      {message.attachments && message.attachments.length > 0 && (
                        <Box sx={{ mt: 1 }}>
                          {message.attachments.map((attachment, index) => (
                            <Chip
                              key={index}
                              icon={<AttachFile />}
                              label={attachment.name}
                              variant="outlined"
                              size="small"
                              sx={{ mr: 1 }}
                              onClick={() => window.open(attachment.url)}
                            />
                          ))}
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
                
                {/* Reply Form */}
                <Box sx={{ pt: 2, borderTop: '1px solid #e0e0e0' }}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    placeholder="Type your reply here..."
                    variant="outlined"
                    value={replyText}
                    onChange={handleReplyChange}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                    <Button
                      startIcon={<AttachFile />}
                      variant="outlined"
                    >
                      Attach File
                    </Button>
                    <Box>
                      <Button
                        variant="outlined"
                        sx={{ mr: 1 }}
                        onClick={() => handleStatusChange('closed')}
                      >
                        Close Ticket
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        endIcon={<Message />}
                        disabled={!replyText.trim()}
                        onClick={handleSendReply}
                      >
                        Reply
                      </Button>
                    </Box>
                  </Box>
                </Box>
              </React.Fragment>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <Assignment sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary">
                  Select a ticket to view details
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  You can manage the ticket, change its status, and respond to the user
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>
      
      {/* Filter Dialog */}
      <Dialog open={filterDialogOpen} onClose={() => setFilterDialogOpen(false)}>
        <DialogTitle>
          Filter Tickets
          <IconButton
            onClick={() => setFilterDialogOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Status</InputLabel>
            <Select
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              label="Status"
            >
              <MenuItem value="">Any</MenuItem>
              <MenuItem value="open">Open</MenuItem>
              <MenuItem value="in-progress">In Progress</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
              <MenuItem value="resolved">Resolved</MenuItem>
              <MenuItem value="closed">Closed</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl fullWidth margin="normal">
            <InputLabel>Priority</InputLabel>
            <Select
              name="priority"
              value={filters.priority}
              onChange={handleFilterChange}
              label="Priority"
            >
              <MenuItem value="">Any</MenuItem>
              <MenuItem value="high">High</MenuItem>
              <MenuItem value="medium">Medium</MenuItem>
              <MenuItem value="low">Low</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl fullWidth margin="normal">
            <InputLabel>Category</InputLabel>
            <Select
              name="category"
              value={filters.category}
              onChange={handleFilterChange}
              label="Category"
            >
              <MenuItem value="">Any</MenuItem>
              <MenuItem value="technical">Technical</MenuItem>
              <MenuItem value="billing">Billing</MenuItem>
              <MenuItem value="trading">Trading</MenuItem>
              <MenuItem value="account">Account</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={clearFilters}>Clear Filters</Button>
          <Button 
            onClick={() => setFilterDialogOpen(false)} 
            variant="contained"
          >
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TicketManagement;
